import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import { getCookie } from 'cookies-next'
import { isEqual, uniqWith } from 'lodash'
import { useRouter } from 'next/router'
import { Button } from '@/atoms/Button'
import { FlexColumn, FlexRow } from '@/atoms/FlexContainers'
import { CaretRightIcon } from '@/atoms/Icons/CaretRightIcon'
import { CircleCheckIcon } from '@/atoms/Icons/CircleCheckIcon'
import { CircleCloseIcon } from '@/atoms/Icons/CircleCloseIcon'
import { FireIcon } from '@/atoms/Icons/FireIcon'
import { LoadingSpinnerIcon } from '@/atoms/Icons/LoadingSpinnerIcon'
import { SEGMENT_ANONYMOUS_ID_COOKIE } from '@/constants/cookies'
import { ExperimentVariation, GBFadeInExperiment } from '@/experimentation'
import { formatPrice } from '@/services/PriceFormatter'
import { ShopifyProductWithBundles } from '@/services/Shopify/ShopifyService'
import { useShopifyCart } from '@/services/Shopify/hooks'
import { MoneyCurrencyCode } from '@/types/codegen-federation'
import { mergeUrlParams } from '@/utils/UriBuilder/UriBuilder'
import { useSafeAnalytics } from '@/utils/analytics'
import { useEcommerceEvents } from '@/utils/analytics/ecommerce-events'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { GiveawaySupportModal } from '@/views/PreSales/PreSalesGiveawayView/components/GiveawaySupportModal'
import { useExperimentData } from '@/views/PreSales/hooks/useExperimentData'
import useHandleUnexpectedError from '@/views/TicketCheckoutViews/hooks/useHandleUnexpectedError'
import { HomesteadEyebrowMD, HomesteadEyebrowSM } from './Typography'

interface Props {
  tickets: ShopifyProductWithBundles[]
  productRef: (node?: Element | null | undefined) => void
  theatricalSlug: string
  multiplier?: number
}

const DEFAULT_ENTRY_PRODUCT_ID = 'gid://shopify/ProductVariant/50315758371047'
const TICKET_TITLE_SET = new Set(['Homestead Ticket', 'Homestead Matinee Ticket'])

export const PresaleChipSelectorCard: FC<Props> = ({ tickets, productRef, theatricalSlug, multiplier }) => {
  const [selectedProduct, setSelectedProduct] = useState<ShopifyProductWithBundles | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false)
  const { t } = useTranslate('tickets')
  const { trackProductAdded } = useEcommerceEvents()
  const { push, pathname } = useRouter()
  const { handleUnexpectedError } = useHandleUnexpectedError({ reservationId: 'unknown' })
  const { createCart } = useShopifyCart()
  const { user } = useSafeAnalytics()
  const entriesPerTicket = 1

  const productTitleToFilter = 'Homestead Ticket'
  const DEFAULT_TICKET_PACK_ID = 'gid://shopify/Product/9042410799335'
  const anonId = getCookie(SEGMENT_ANONYMOUS_ID_COOKIE)

  const getExperimentData = useExperimentData({
    funnel: 'theatrical-giveaway',
    category: 'theatrical giveaway ticket',
    project_slug: theatricalSlug,
    anon_id: anonId ?? user()?.anonymousId,
    zac_is: 'so_cool',
  })

  const selectedQty = useMemo(() => {
    return (
      selectedProduct?.bundledProducts?.find((product) => TICKET_TITLE_SET.has(product?.product?.title))?.quantity ?? 0
    )
  }, [selectedProduct?.bundledProducts])

  useEffect(() => {
    const isSameProductList = tickets?.some((product) => product.id === selectedProduct?.id)

    if (isSameProductList) return

    const offer = tickets?.find((offer) => {
      if (selectedQty) {
        return offer?.bundledProducts?.find((product) => product?.quantity === selectedQty)
      }
      return offer?.id === DEFAULT_TICKET_PACK_ID
    })
    if (offer) setSelectedProduct(offer)
  }, [tickets, DEFAULT_TICKET_PACK_ID, selectedQty, selectedProduct?.id])

  const handleCheckout = useCallback(
    async (product: ShopifyProductWithBundles) => {
      const attributes = getExperimentData()
      const lines: Array<{ quantity: number; merchandiseId: string; sellingPlanId?: string }> = [
        { quantity: 1, merchandiseId: product?.variants?.edges[0]?.node?.id },
        {
          quantity: selectedQty * entriesPerTicket * (multiplier ?? 1),
          merchandiseId: DEFAULT_ENTRY_PRODUCT_ID,
        },
      ]

      try {
        setIsLoading(true)

        const { cart, errors } = await createCart({
          lines,
          attributes,
        })

        if (errors && errors.length > 0) {
          setIsSupportModalOpen(true)
          return
        }

        const checkoutUrl = mergeUrlParams(cart?.checkoutUrl, { utm_term: 'from-angel-com-flow' })

        if (checkoutUrl) {
          trackProductAdded({
            category: 'theatrical pre-order ticket',
            funnel: 'theatrical-pre-sale',
            sku: `pre-order-${theatricalSlug}`,
            product_id: `pre-order-${theatricalSlug}`,
            name: `${theatricalSlug} theatrical pre-order ticket`,
            price: parseInt(product?.priceRange?.maxVariantPrice?.amount ?? 0),
            currency: (product?.priceRange?.maxVariantPrice?.currencyCode as MoneyCurrencyCode) ?? 'USD',
            quantity: 1,
            projectSlug: theatricalSlug,
          })
          return push(checkoutUrl)
        }
      } catch (error) {
        handleUnexpectedError({
          context: 'AddPreOrderTicketOption',
          error,
          customToastMessage: t('somethingWentWrongPleaseTryAgainPreOrder', 'Something went wrong, please try again.'),
        })
      } finally {
        setIsLoading(false)
      }
    },
    [
      createCart,
      entriesPerTicket,
      getExperimentData,
      handleUnexpectedError,
      multiplier,
      push,
      selectedQty,
      t,
      theatricalSlug,
      trackProductAdded,
    ],
  )

  const ticketQuantityList = useMemo(() => {
    return tickets
      ?.map((offer) => {
        return {
          id: offer.id,
          quantity:
            offer?.bundledProducts?.find((bundle) => bundle?.product?.title === productTitleToFilter)?.quantity ?? 0,
        }
      })
      ?.sort((a, b) => a.quantity - b.quantity)
  }, [tickets, productTitleToFilter])

  const totalEntries = useCallback(() => {
    return selectedQty * entriesPerTicket * (multiplier || 1)
  }, [selectedQty, entriesPerTicket, multiplier])

  const allPerks = useMemo(() => {
    const getDynamicPerks = (qty: number, entriesFunc: () => number) => [
      {
        id: 'dynamic-tickets',
        title: t('homesteadMovieTicketsCount', '{{count}} Homestead Movie Tickets', { count: qty }),
      },
      {
        id: 'dynamic-entries',
        title: t('giveawayEntriesCount', '{{count}} Giveaway Entries', { count: entriesFunc() }),
      },
    ]

    const perks = tickets
      ?.flatMap((product) => product?.bundledProducts)
      ?.map((bundle) => bundle?.product)
      ?.filter((product) => product?.title !== productTitleToFilter)

    const digitalBook = perks?.find((perk) => perk?.title?.includes('Digital Edition'))
    const filterOutDigitalBook = digitalBook ? perks?.filter((perk) => perk?.id !== digitalBook?.id) : perks

    return [...getDynamicPerks(selectedQty, totalEntries), digitalBook, ...uniqWith(filterOutDigitalBook, isEqual)]
  }, [tickets, selectedQty, totalEntries, t])

  const activePerkIds = useMemo(() => {
    return new Set([
      ...(allPerks ?? [])
        .filter((perk) => selectedProduct?.bundledProducts?.some((product) => product?.product?.id === perk?.id))
        .map((perk) => perk?.id),
    ])
  }, [allPerks, selectedProduct?.bundledProducts])

  const isPerkActive = useCallback(
    (perkId: string) => {
      if (perkId === 'dynamic-tickets' || perkId === 'dynamic-entries') {
        return true
      }
      return activePerkIds.has(perkId)
    },
    [activePerkIds],
  )

  const handlePackClick = useCallback(
    (offerId: string) => {
      const offerToSelect = tickets.find((product) => product?.id === offerId) as ShopifyProductWithBundles
      if (offerToSelect) setSelectedProduct(offerToSelect)
    },
    [tickets],
  )

  const handlePerkClick = useCallback(
    (perkId: string) => {
      const shouldSkip = !!selectedProduct?.bundledProducts?.some((offer) => offer?.product?.id === perkId)
      if (shouldSkip) return

      const productToSelect = tickets?.find((offer) =>
        offer?.bundledProducts?.some((product) => product?.product?.id === perkId),
      )
      if (productToSelect) setSelectedProduct(productToSelect)
    },
    [selectedProduct?.bundledProducts, tickets],
  )

  const isLowerPricedTickets = useMemo(() => {
    return selectedQty >= 30
  }, [selectedQty])

  function handleInitialCheckoutIntent(): void {
    selectedProduct && handleCheckout(selectedProduct)
  }

  return (
    <>
      <div className="flex flex-col">
        <div
          ref={productRef}
          className="mx-auto flex max-w-[420px] flex-col gap-6 rounded-2xl border border-[#E5E8EF] bg-[#F5F5F5] px-8 py-6 md:rounded-3xl md:bg-white"
        >
          <div className="flex flex-col items-center gap-1">
            <p
              className={classNames(
                'homestead-eyebrow-md mb-1 rounded-md bg-homestead-yellow px-2 py-1 uppercase text-homestead-primary-blue',
                isLowerPricedTickets ? 'visible' : 'invisible',
              )}
            >
              {t('tenPerTicket', '$10 per ticket')}
            </p>

            <FlexRow className="!items-end gap-3">
              <p className="items-top flex !font-[dharma-gothic-e] text-[3.125rem] !font-extrabold leading-[50px] tracking-[1.75px] text-homestead-primary-blue">
                <span className='"!font-[dharma-gothic-e] tracking-[28px]" block translate-y-1/4 text-[1.75rem] !font-extrabold leading-[1.5px]'>
                  $
                </span>
                {formatPrice((selectedProduct?.priceRange?.maxVariantPrice?.amount ?? 0) * 100, {
                  currency: selectedProduct?.priceRange?.maxVariantPrice?.currencyCode ?? 'USD',
                  ignoreEmptyDecimal: true,
                  style: 'decimal',
                })}
              </p>
            </FlexRow>
            <FlexColumn className="text-center">
              <p className="homestead-title-xs !font-normal text-homestead-blue">
                {t('fullPriceTicketsSelectedQty', '{{selectedQty}} Tickets', { selectedQty })}
              </p>
            </FlexColumn>
            {pathname.includes('giveaway') && (
              <FlexColumn className="mt-2">
                <div className="flex flex-col items-center">
                  <HomesteadEyebrowMD className="rounded-md bg-homestead-blue px-2 pb-[5px] pt-1" color="white">
                    {totalEntries()} {t('giveawayEntriesV2', 'GIVEAWAY ENTRIES')}
                  </HomesteadEyebrowMD>
                  <HomesteadEyebrowSM className="mt-2 !text-[11px] italic">
                    Includes <span className="font-bold text-homestead-red">{multiplier}x bonus entries</span> this week
                    only!
                  </HomesteadEyebrowSM>
                </div>
              </FlexColumn>
            )}
          </div>

          <div className="flex flex-col gap-4">
            <p className="homestead-eyebrow-md !font-semibold uppercase text-homestead-primary-blue">
              {t('ticketQuantityPreSales', 'Ticket Quantity')}:
            </p>
            <ul className="flex grow-1 flex-wrap justify-center gap-3">
              {ticketQuantityList.map((ticket) => {
                const isSelectedOffer = ticket?.id === selectedProduct?.id
                return (
                  <li className="!w-full min-w-[64px] max-w-[64px] md:!max-w-[70px]" key={ticket.id}>
                    <Button
                      onClick={() => handlePackClick(ticket.id)}
                      className={classNames(
                        'rounded-full !text-sm !border-none h-[40px] grow-1 !w-full relative',
                        isSelectedOffer ? '!bg-homestead-primary-blue !text-white' : '!bg-[#D3D3D6]/70',
                      )}
                      variant="lightgray"
                    >
                      {ticket.quantity}
                      {ticket.quantity === 4 && (
                        <GBFadeInExperiment defaultValue={false} experimentName={'presale_fire_icon_on_4_tickets'}>
                          <ExperimentVariation variation={true}>
                            <div className="absolute -right-1 -top-1 animate-wiggle rounded-full border-[1.5px] border-warm-01 bg-warm-01 p-1">
                              <FireIcon color="white" size={12} />
                            </div>
                          </ExperimentVariation>
                        </GBFadeInExperiment>
                      )}
                    </Button>
                  </li>
                )
              })}
            </ul>
          </div>

          <div className="flex flex-col gap-4">
            <p className="homestead-eyebrow-md !font-semibold uppercase text-homestead-primary-blue">
              {t('superFanBenefits', 'Superfan Benefits')}: ({activePerkIds?.size + 2} / {allPerks?.length})
            </p>
            <ul className="mx-2 flex flex-col gap-2">
              {allPerks?.map((perk) => {
                const isActive = isPerkActive(perk?.id ?? '')
                return (
                  <li
                    onClick={() => handlePerkClick(perk?.id as string)}
                    key={perk?.id}
                    className={classNames('flex items-start gap-2 cursor-pointer', !isActive && 'opacity-40')}
                  >
                    <div className="shrink-0 pt-[4px]">
                      {isActive ? (
                        <CircleCheckIcon
                          className="shrink-0"
                          size={14}
                          color2="white"
                          color1="homestead-primary-blue"
                        />
                      ) : (
                        <CircleCloseIcon className="shrink-0" color="homestead-blue" size={14} />
                      )}
                    </div>
                    <p
                      className={classNames(
                        '!font-[bio-sans] text-sm leading-[18.2px]',
                        isActive ? 'text-homestead-primary-blue' : 'text-homestead-blue',
                      )}
                    >
                      {perk?.title}
                    </p>
                  </li>
                )
              })}
            </ul>
          </div>

          {selectedProduct && (
            <button
              disabled={isLoading}
              onClick={() => handleInitialCheckoutIntent()}
              className="!homestead-btn-sm flex w-full items-center justify-between rounded-[14px] !bg-homestead-red px-5 py-[0.906rem] !font-semibold uppercase !text-white"
            >
              <span className="flex items-center gap-2">
                {t('Next', 'Next')}
                {isLoading && <LoadingSpinnerIcon size={18} color="white" />}
              </span>
              <span className="gap flex items-center text-white/80">
                {formatPrice((selectedProduct?.priceRange?.maxVariantPrice?.amount ?? 0) * 100, {
                  currency: selectedProduct?.priceRange?.maxVariantPrice?.currencyCode ?? 'USD',
                  ignoreEmptyDecimal: true,
                })}
                <CaretRightIcon color="white" className="opacity-80" size={28} />
              </span>
            </button>
          )}
        </div>
        <p className="mx-auto mt-6 max-w-[420px] px-4 !font-[bio-sans] text-xs font-normal leading-[18px] text-homestead-blue">
          <Translate t={t} i18nKey="presaleChipDisclaimer">
            Your ticket codes will be emailed after purchase. Redeem them at{' '}
            <a href="https://angel.com/tickets" target="_blank" className="underline">
              angel.com/tickets
            </a>{' '}
            when showtimes are available. Questions? Check our FAQs!
          </Translate>
        </p>
      </div>

      <GiveawaySupportModal
        open={isSupportModalOpen}
        onClose={() => {
          setIsSupportModalOpen(false)
        }}
      />
    </>
  )
}
